import {
  Button,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Switch,
  Upload,
  message,
} from "antd";
import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { TimePicker } from "../../components/inputs/MomentDatePicker";
import { uploadDoc } from "../../services/upload.service";
import { createGroupWakeupAlarm } from "../../services/groupConsole.service";
import reportCreateResult from "./reportCreateResult";
import useTargetSchools from "./useTargetSchools";

const { TextArea } = Input;

// Matches managealarm/AddEditAlarmModal.js's WEEKDAYS — 0 = Sunday … 6 =
// Saturday, the same numbering the backend cron compares against.
const WEEKDAYS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

const TIMEZONES = (() => {
  try {
    const values = Intl.supportedValuesOf?.("timeZone");
    if (values?.length) return values;
  } catch {
    // fall through
  }
  return ["Asia/Kolkata", "Asia/Dubai", "Asia/Colombo", "Asia/Singapore"];
})();

/**
 * Trimmed relative to the branch admin's AddEditAlarmModal.js: no scope
 * radio, no class picker, and audio/voice are both plain file uploads with
 * no in-browser recorder. A class-scoped alarm needs a branch's own class
 * list, which doesn't line up across branches — so this always creates a
 * school-wide alarm (`scope: "school"`), regardless of how many branches are
 * targeted.
 */
export default function CreateGroupWakeupAlarmModal({ groupId, open, onClose }) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const { targetSchools, selectProps } = useTargetSchools();
  const [recurrenceType, setRecurrenceType] = useState("daily");
  const [audio, setAudio] = useState(null);
  const [voice, setVoice] = useState(null);
  const [uploading, setUploading] = useState(false);

  const close = () => {
    form.resetFields();
    setAudio(null);
    setVoice(null);
    setRecurrenceType("daily");
    onClose();
  };

  const createMutation = useMutation(
    (payload) => createGroupWakeupAlarm(groupId, payload),
    {
      onSuccess: (result) => {
        reportCreateResult(result, "Alarm");
        queryClient.invalidateQueries("group-console-wakeup-alarms");
        close();
      },
      onError: (err) => {
        message.error(
          err?.response?.data?.error?.errors?.[0] || "Failed to create alarm",
        );
      },
    },
  );

  const uploadTone = (setter) => async ({ file, onSuccess, onError }) => {
    if (!file.type?.startsWith("audio/")) {
      message.error("Please choose an audio file (mp3, m4a, wav).");
      return onError(new Error("not audio"));
    }
    if (file.size > 5 * 1024 * 1024) {
      message.error("Alarm audio must be 5 MB or smaller.");
      return onError(new Error("too large"));
    }
    setUploading(true);
    try {
      const result = await uploadDoc({ file }, "alarm-audio");
      setter({ id: result.id, name: file.name, type: "AUDIO", url: result.files.original });
      onSuccess(null, file);
    } catch (err) {
      message.error("Failed to upload the alarm tone");
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  const onFinish = (values) => {
    createMutation.mutate({
      scope: "school",
      class_ids: [],
      title: values.title,
      message: values.message,
      recurrence: {
        type: values.recurrence_type,
        at_time: values.at_time.format("HH:mm"),
        days_of_week: values.recurrence_type === "weekly" ? values.days_of_week : [],
      },
      timezone: values.timezone,
      snooze_minutes: values.snooze_minutes,
      max_snoozes: values.max_snoozes,
      skip_on_holiday: values.skip_on_holiday,
      target_roles: values.target_roles,
      is_active: values.is_active,
      audio: audio || null,
      voice: voice || null,
      targetSchools,
    });
  };

  return (
    <Modal
      title="Create Wake-up Alarm"
      open={open}
      onCancel={close}
      footer={null}
      width={640}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          timezone: "Asia/Kolkata",
          recurrence_type: "daily",
          snooze_minutes: 5,
          max_snoozes: 3,
          target_roles: ["student", "parent"],
          skip_on_holiday: true,
          is_active: true,
        }}
      >
        <Form.Item label="Post to" required>
          <Select style={{ width: "100%" }} {...selectProps} />
        </Form.Item>
        <div className="text-xs text-gray-400 mb-4 -mt-2">
          Always a whole-school alarm — a class-scoped alarm needs that
          branch's own class list, which isn't available when targeting more
          than one branch.
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            label="Wake-up time"
            name="at_time"
            rules={[{ required: true, message: "Pick a time" }]}
          >
            <TimePicker format="HH:mm" minuteStep={5} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            label="Timezone"
            name="timezone"
            rules={[{ required: true, message: "Pick a timezone" }]}
          >
            <Select
              showSearch
              optionFilterProp="value"
              options={TIMEZONES.map((tz) => ({ label: tz, value: tz }))}
            />
          </Form.Item>
        </div>

        <Form.Item label="Repeats" name="recurrence_type">
          <Radio.Group onChange={(e) => setRecurrenceType(e.target.value)}>
            <Radio.Button value="daily">Every day</Radio.Button>
            <Radio.Button value="weekdays">Mon–Fri</Radio.Button>
            <Radio.Button value="weekly">Chosen days</Radio.Button>
          </Radio.Group>
        </Form.Item>

        {recurrenceType === "weekly" && (
          <Form.Item
            label="Days"
            name="days_of_week"
            rules={[
              {
                validator: (_, value) =>
                  value && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Select at least one day")),
              },
            ]}
          >
            <Checkbox.Group options={WEEKDAYS} />
          </Form.Item>
        )}

        <Form.Item
          label="Title"
          name="title"
          rules={[{ required: true, message: "Enter a title" }]}
        >
          <Input maxLength={60} placeholder="Good morning!" />
        </Form.Item>

        <Form.Item
          label="Message"
          name="message"
          rules={[{ required: true, message: "Enter a message" }]}
        >
          <TextArea rows={2} maxLength={160} />
        </Form.Item>

        <Form.Item label="Alarm tone (file upload only)">
          <Upload customRequest={uploadTone(setAudio)} showUploadList={false} accept="audio/*">
            <Button loading={uploading}>{audio ? "Replace file" : "Upload file"}</Button>
          </Upload>
          {audio && <span className="text-xs text-gray-500 ml-3">{audio.name}</span>}
        </Form.Item>

        <Form.Item label="Voice tone (file upload only)">
          <Upload customRequest={uploadTone(setVoice)} showUploadList={false} accept="audio/*">
            <Button loading={uploading}>{voice ? "Replace file" : "Upload file"}</Button>
          </Upload>
          {voice && <span className="text-xs text-gray-500 ml-3">{voice.name}</span>}
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Snooze length (minutes)" name="snooze_minutes">
            <InputNumber min={1} max={30} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item label="Snoozes allowed" name="max_snoozes">
            <InputNumber min={0} max={10} style={{ width: "100%" }} />
          </Form.Item>
        </div>

        <Form.Item
          label="Send to"
          name="target_roles"
          rules={[
            {
              validator: (_, value) =>
                value && value.length > 0
                  ? Promise.resolve()
                  : Promise.reject(new Error("Choose at least one recipient")),
            },
          ]}
        >
          <Checkbox.Group
            options={[
              { label: "Students", value: "student" },
              { label: "Parents", value: "parent" },
            ]}
          />
        </Form.Item>

        <Form.Item label="Skip on school holidays" name="skip_on_holiday" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item label="Active" name="is_active" valuePropName="checked">
          <Switch />
        </Form.Item>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button onClick={close}>Cancel</Button>
          <Button type="primary" htmlType="submit" loading={createMutation.isLoading}>
            Create
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
